import React from "react";
import { Bot } from "lucide-react";
import { Agent } from "../../model_gen/agent_base";
import { cn } from "../../lib/utils";

interface AgentListProps {
  agents: Agent[];
  searchQuery: string;
  selectedIndex?: number;
  onSelect: (agent: Agent) => void;
  className?: string;
}

export const AgentList: React.FC<AgentListProps> = ({
  agents,
  searchQuery,
  selectedIndex = 0,
  onSelect,
  className
}) => {
  // Filter agents based on search query
  const filteredAgents = agents.filter(agent => {
    // Use displayName if available, otherwise fall back to agent_name
    const displayName = (agent as any).displayName || agent.agent_name;
    const nameMatch = displayName.toLowerCase().includes(searchQuery.toLowerCase());
    const descMatch = (agent.description?.toLowerCase() || "").includes(searchQuery.toLowerCase());
    return nameMatch || descMatch;
  });
  
  // Scroll selected item into view
  React.useEffect(() => {
    const selectedElement = document.querySelector(`[data-agent-index="${selectedIndex}"]`);
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedIndex]);

  if (filteredAgents.length === 0) {
    return (
      <div className={cn("p-4 text-center", className)}>
        <Bot className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">No agents found</p>
        <p className="text-xs text-muted-foreground/70 mt-1">Try a different search term</p>
      </div>
    );
  }

  return (
    <div className={cn("py-1", className)}>
      {filteredAgents.map((agent, index) => (
        <button
          key={agent.id}
          data-agent-index={index}
          onClick={() => onSelect(agent)}
          className={cn(
            "w-full text-left px-3 py-2 group relative overflow-hidden",
            "hover:bg-accent hover:text-accent-foreground",
            "transition-all duration-150 ease-out",
            "focus:outline-none",
            index === selectedIndex && "bg-accent text-accent-foreground",
            index !== filteredAgents.length - 1 && "border-b border-border/50"
          )}
          style={{
            ...(index === selectedIndex && {
              borderLeft: "2px solid #10b981",
              paddingLeft: "9px" // Adjust padding to compensate for border
            })
          }}
        >
          {/* Hover indicator - only show if not selected */}
          {index !== selectedIndex && (
            <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-maitai-green scale-y-0 group-hover:scale-y-100 transition-transform duration-150" />
          )}
          
          {/* Agent/Action name */}
          <p className="text-sm font-medium truncate">
            {(agent as any).displayName || agent.agent_name}
          </p>
          
          {/* Description that appears on hover or selection */}
          {agent.description && (
            <div className={cn(
              "transition-all duration-150 ease-out overflow-hidden",
              index === selectedIndex ? "max-h-20" : "max-h-0 group-hover:max-h-20"
            )}>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                {agent.description.length > 120 
                  ? `${agent.description.substring(0, 120)}...` 
                  : agent.description
                }
              </p>
            </div>
          )}
        </button>
      ))}
    </div>
  );
};
